/**
 * Resolve Finance Payment Settings → source COA for a CRM payment method.
 * Mirrors crm-service financeIntegrationService.findPaymentMethodMapping.
 */

export const normalizeCrmPaymentMethodForFinance = (method) => {
  const value = String(method || '').trim().toLowerCase();
  if (!value) return 'OTHER';
  if (value.includes('cash')) return 'CASH';
  if (value.includes('card') || value.includes('visa') || value.includes('master')) return 'CARD';
  if (value.includes('cheque') || value.includes('check')) return 'CHEQUE';
  if (value.includes('mobile')) return 'MOBILE_PAYMENT';
  if (value.includes('online') || value.includes('paycorp')) return 'ONLINE_PAYMENT';
  if (value.includes('bank') || value.includes('transfer')) return 'BANK_TRANSFER';
  return 'OTHER';
};

const resolveMappingFromList = (mappings, normalizedMethod) => {
  const list = Array.isArray(mappings) ? mappings : [];
  const mapping = list.find((entry) => entry.method === normalizedMethod) || null;
  if (mapping) return mapping;
  if (normalizedMethod === 'CARD') {
    return list.find((entry) => entry.method === 'CREDIT_CARD')
      || list.find((entry) => entry.method === 'DEBIT_CARD')
      || null;
  }
  return null;
};

export const findFinancePaymentMethodMapping = (financeSettings, paymentMethod, locationId = null) => {
  const normalizedMethod = normalizeCrmPaymentMethodForFinance(paymentMethod);
  if (locationId) {
    const locationEntry = (financeSettings?.locationPaymentMethodMappings || []).find(
      (entry) => String(entry.locationId) === String(locationId)
    );
    if (!locationEntry) return null;
    return resolveMappingFromList(locationEntry.paymentMethodMappings, normalizedMethod);
  }
  return resolveMappingFromList(financeSettings?.paymentMethodMappings, normalizedMethod);
};

/** Prefer T&W payment-method mappings, then fall back to CRM Lead Application mappings. */
export const findTwFinancePaymentMethodMapping = (financeSettings, paymentMethod, locationId = null) => {
  const normalizedMethod = normalizeCrmPaymentMethodForFinance(paymentMethod);
  const fromEntry = (entry) => {
    if (!entry) return null;
    return resolveMappingFromList(entry.twPaymentMethodMappings, normalizedMethod)
      || resolveMappingFromList(entry.paymentMethodMappings, normalizedMethod);
  };
  if (locationId) {
    const locationEntry = (financeSettings?.locationPaymentMethodMappings || []).find(
      (entry) => String(entry.locationId) === String(locationId)
    );
    const mapped = fromEntry(locationEntry);
    if (mapped) return mapped;
  }
  return resolveMappingFromList(financeSettings?.twPaymentMethodMappings, normalizedMethod)
    || resolveMappingFromList(financeSettings?.paymentMethodMappings, normalizedMethod);
};

export const formatFinanceSourceAccountLabel = (mapping) => {
  if (!mapping) return '';
  const id = mapping.sourceAccountId?._id || mapping.sourceAccountId;
  if (!id) return '';
  const code = mapping.sourceAccountCode || mapping.sourceAccountId?.accountCode || '';
  const name = mapping.sourceAccountName || mapping.sourceAccountId?.accountName || '';
  if (code && name) return `${code} - ${name}`;
  return name || code || String(id);
};

export const extractFinanceSourceAccountId = (mapping) => {
  if (!mapping) return '';
  return String(mapping.sourceAccountId?._id || mapping.sourceAccountId || '');
};

/** YYYY-MM-DD for <input type="date"> from a Date / ISO string */
export const toDateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** YYYY-MM-DDTHH:mm for <input type="datetime-local"> (local timezone) */
export const toDateTimeInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

/** Convert datetime-local value to ISO string for API storage */
export const dateTimeLocalToIso = (value) => {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/**
 * Public payment forms use <input type="date"> (YYYY-MM-DD). Combine that
 * calendar day with the current local clock so paymentDate stores a real
 * datetime for approve defaults / GL posting.
 */
export const toPaymentDateTimeIso = (dateOrDatetime) => {
  if (!dateOrDatetime) return new Date().toISOString();
  const raw = String(dateOrDatetime).trim();
  const now = new Date();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const local = new Date(
      y,
      m - 1,
      d,
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );
    return local.toISOString();
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return now.toISOString();
  return parsed.toISOString();
};

const isUtcMidnightDateOnly = (d) =>
  d instanceof Date &&
  !Number.isNaN(d.getTime()) &&
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

/** Default datetime-local value for approve "Payment received date & time". */
export const defaultApproveReceivedDateTime = (payment) => {
  if (payment?.receivedDate) return toDateTimeInputValue(payment.receivedDate);

  const pd = payment?.paymentDate ? new Date(payment.paymentDate) : null;
  if (pd && !Number.isNaN(pd.getTime())) {
    if (!isUtcMidnightDateOnly(pd)) return toDateTimeInputValue(pd);

    // Legacy date-only rows: keep the payment calendar day, use submit clock from ObjectId.
    const hex = payment?._id != null ? String(payment._id) : '';
    if (/^[a-fA-F0-9]{24}$/.test(hex)) {
      const submitted = new Date(parseInt(hex.substring(0, 8), 16) * 1000);
      if (!Number.isNaN(submitted.getTime())) {
        const merged = new Date(
          pd.getUTCFullYear(),
          pd.getUTCMonth(),
          pd.getUTCDate(),
          submitted.getHours(),
          submitted.getMinutes(),
          submitted.getSeconds()
        );
        return toDateTimeInputValue(merged);
      }
    }
    return toDateTimeInputValue(pd);
  }

  return toDateTimeInputValue(new Date());
};

/** Payload fragment for approve APIs that accept GL COA + received datetime. */
export const buildApproveFinancePayload = ({ receivedDate, glSourceAccountId } = {}) => {
  const payload = {};
  const iso = dateTimeLocalToIso(receivedDate);
  if (iso) payload.receivedDate = iso;
  if (glSourceAccountId) payload.glSourceAccountId = String(glSourceAccountId);
  return payload;
};

/** Normalize Events payment method keys to Events settings mapping methods. */
export const normalizeEventPaymentMethodForFinance = (method) => {
  const value = String(method || '').trim().toUpperCase();
  if (!value) return '';
  if (value === 'CHEQUE' || value.includes('CHEQUE')) return 'CHEQUE_DEPOSIT';
  if (value === 'CARD_OFFLINE' || value.includes('CARD')) return 'CARD';
  if (value.includes('CASH')) return 'CASH';
  if (value.includes('BANK') || value.includes('TRANSFER')) return 'BANK_TRANSFER';
  if (value.includes('OTHER')) return 'OTHER';
  return value;
};

/** Resolve Events Settings financeMapping.paymentMethodMappings row for a method. */
export const findEventFinancePaymentMethodMapping = (eventSettings, paymentMethod) => {
  const normalized = normalizeEventPaymentMethodForFinance(paymentMethod);
  const mappings = eventSettings?.financeMapping?.paymentMethodMappings
    || eventSettings?.paymentMethodMappings
    || [];
  const list = Array.isArray(mappings) ? mappings : [];
  return list.find((row) => String(row?.method || '').toUpperCase() === normalized) || null;
};
